import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import api from '../api/api';
import './OperatingDishCalculations.css';

type Result = {
  inputFingerprint: string; inputsJson: string; costPrice: number;
  marginType: string; margin: number; salePrice: number;
};
type Calculation = Result & { id: number; groupVersion: number; createdAtUtc: string; createdByUserId: string };
type History = {
  version: number; activeCalculationId: number | null;
  currentInputFingerprint: string | null; inputError: string | null; calculations: Calculation[];
  publications: Array<{ id: number; groupVersion: number; calculationId: number | null; createdAtUtc: string }>;
};
type Props = {
  groups: Array<{ id: number; name: string; isArchived: boolean }>;
  dishes: Array<{ id: number; name: string }>;
};
const message = (error: unknown, fallback: string) =>
  axios.isAxiosError<{ message?: string }>(error) && error.response?.data?.message
    ? error.response.data.message : fallback;

function CalculationInputs({ inputsJson, fingerprint }: { inputsJson: string; fingerprint: string }) {
  let formatted: string;
  try {
    const inputs: unknown = JSON.parse(inputsJson);
    formatted = JSON.stringify(inputs, null, 2);
  } catch (error) {
    if (!(error instanceof SyntaxError)) throw error;
    return <p role="alert">Збережені вхідні дані пошкоджені. Не використовуйте цей знімок без перевірки.</p>;
  }
  return <details><summary>Точні вхідні дані та fingerprint</summary>
    <p className="operating-calculations__fingerprint">{fingerprint}</p><pre>{formatted}</pre>
  </details>;
}

export default function OperatingDishCalculations({ groups, dishes }: Props) {
  const [groupId, setGroupId] = useState('');
  const [dishId, setDishId] = useState('');
  const [marginType, setMarginType] = useState('percent');
  const [margin, setMargin] = useState('0');
  const [history, setHistory] = useState<History | null>(null);
  const [preview, setPreview] = useState<Result | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [access, setAccess] = useState<{ view: boolean; edit: boolean } | null>(null);
  const request = useRef(0);
  const endpoint = `/operating-groups/${groupId}/dishes/${dishId}/calculations`;

  useEffect(() => {
    let current = true;
    api.get<{ permissions: string[]; editPermissions: string[] }>('/users/me/access').then(({ data }) => {
      if (!current) return;
      const required = ['page.admin', 'page.management.dishes'];
      setAccess({
        view: required.every((key) => data.permissions.includes(key)),
        edit: required.every((key) => data.permissions.includes(key) && data.editPermissions.includes(key)),
      });
    }).catch(() => { if (current) setError('Не вдалося перевірити права розрахунків.'); });
    return () => { current = false; request.current += 1; };
  }, []);

  const reload = useCallback(async (): Promise<boolean> => {
    const identity = ++request.current;
    setHistory(null);
    setPreview(null);
    if (!groupId || !dishId || !access?.view) { setBusy(false); return false; }
    setBusy(true);
    try {
      const { data } = await api.get<History>(endpoint);
      if (identity !== request.current) return false;
      setHistory(data);
      return true;
    } catch (loadError) {
      if (identity === request.current) setError(message(loadError, 'Не вдалося завантажити розрахунки.'));
      return false;
    } finally { if (identity === request.current) setBusy(false); }
  }, [groupId, dishId, endpoint, access?.view]);

  useEffect(() => { void reload(); }, [reload]);
  const changeIdentity = (group: string, dish: string) => {
    request.current += 1;
    setHistory(null); setPreview(null); setError(''); setStatus('');
    setGroupId(group); setDishId(dish);
  };
  const ready = Boolean(history && !busy);
  const validMargin = margin.trim() !== '' && Number.isFinite(Number(margin)) &&
    Number(margin) >= 0 && Number(margin) <= 1000000;

  const calculate = async () => {
    if (!ready || !validMargin) return;
    const identity = ++request.current;
    setBusy(true); setPreview(null); setError(''); setStatus('');
    try {
      const { data } = await api.get<Result>(`${endpoint}/preview`, { params: { marginType, margin: Number(margin) } });
      if (identity === request.current) setPreview(data);
    } catch (previewError) {
      if (identity === request.current) setError(message(previewError, 'Розрахунок недоступний.'));
    } finally { if (identity === request.current) setBusy(false); }
  };

  const mutate = async (publication: boolean, calculationId?: number | null) => {
    if (!ready || !access?.edit || (!publication && !preview)) return;
    const identity = ++request.current;
    setBusy(true); setError(''); setStatus('');
    try {
      await api.post(publication ? `${endpoint}/publication` : endpoint, publication
        ? { version: history!.version, calculationId }
        : { version: history!.version, inputFingerprint: preview!.inputFingerprint,
          marginType: preview!.marginType, margin: preview!.margin });
    } catch (saveError) {
      if (identity !== request.current) return;
      const conflict = axios.isAxiosError(saveError) && saveError.response?.status === 409;
      setError(message(saveError, 'Не вдалося зберегти зміну. Перевірте стан перед повтором.'));
      // A response may have been lost after commit: every failed mutation requires reconciliation.
      setHistory(null); setPreview(null); setBusy(false);
      if (conflict) setStatus('Конфлікт: оновіть дані й явно повторно оберіть дію. Автоматичного повтору немає.');
      return;
    }
    if (identity !== request.current) return;
    const loaded = await reload();
    if (request.current !== identity + 1) return;
    setStatus(loaded ? 'Зміну збережено.' :
      'Зміну збережено, але актуальний стан не завантажено. Оновіть дані перед наступною зміною.');
  };

  const download = async () => {
    if (!ready) return;
    const identity = ++request.current;
    setBusy(true); setError('');
    try {
      const { data } = await api.get<Blob>(`${endpoint}/export`, { responseType: 'blob' });
      if (identity !== request.current) return;
      const url = URL.createObjectURL(data);
      const anchor = document.createElement('a');
      anchor.href = url; anchor.download = `group-${groupId}-dish-${dishId}-calculations.json`;
      anchor.click(); URL.revokeObjectURL(url);
    } catch (exportError) {
      if (identity === request.current) setError(message(exportError, 'Не вдалося експортувати розрахунки.'));
    } finally { if (identity === request.current) setBusy(false); }
  };

  return (
    <section className="operating-calculations" aria-labelledby="operating-calculations-title">
      <h3 id="operating-calculations-title">Контекстні розрахунки страв</h3>
      <p>Поточна рецептура та глобальні ціни продуктів, не складські партії. Послуги й інші позиції меню
        поза цим розрахунком: їхні ціни не змінюються. Потрібен необмежений контекстний доступ.</p>
      <p>Збереження не активує ціну. Активація діє лише для явно ввімкненого каталогу групи у залах,
        за чинного глобального затвердження та вибору страви. Активна ціна заморожена до заміни або вимкнення.</p>
      {error && <p role="alert">{error}</p>}
      {status && <p role="status">{status}</p>}
      {access && !access.view && <p>Потрібні права перегляду адмінпанелі та страв.</p>}
      {access?.view && <>
        {!access.edit && <p>Режим перегляду: збереження й активація недоступні.</p>}
        <div className="operating-calculations__fields">
          <label>Група розрахунку<select aria-label="Група розрахунку" value={groupId} disabled={busy}
            onChange={(event) => changeIdentity(event.target.value, dishId)}>
            <option value="">Оберіть групу</option>
            {groups.map((group) => <option key={group.id} value={group.id}>{group.name}{group.isArchived ? ' (архів)' : ''}</option>)}
          </select></label>
          <label>Страва розрахунку<select aria-label="Страва розрахунку" value={dishId} disabled={busy}
            onChange={(event) => changeIdentity(groupId, event.target.value)}>
            <option value="">Оберіть страву</option>
            {dishes.map((dish) => <option key={dish.id} value={dish.id}>{dish.name}</option>)}
          </select></label>
        </div>
        {groupId && dishId && <button type="button" disabled={busy} onClick={() => {
          setError(''); setStatus(''); void reload();
        }}>Оновити розрахунки</button>}
        {busy && <p role="status">Завантаження...</p>}
        {history && <>
          <p>Версія каталогу: {history.version}. Активний розрахунок: {history.activeCalculationId ?? 'немає — глобальна ціна'}.</p>
          {history.inputError && <p role="alert">Поточні вхідні дані недоступні: {history.inputError}</p>}
          <fieldset disabled={!ready}>
            <legend>Попередній розрахунок</legend>
            <div className="operating-calculations__fields">
              <label>Тип націнки<select value={marginType} onChange={(event) => {
                setMarginType(event.target.value); setPreview(null);
              }}><option value="percent">Відсоток, %</option><option value="amount">Сума, ₴</option></select></label>
              <label>Націнка<input type="number" min="0" max="1000000" step="any" value={margin}
                onChange={(event) => { setMargin(event.target.value); setPreview(null); }} /></label>
            </div>
            <button type="button" disabled={!validMargin} onClick={() => void calculate()}>Розрахувати</button>
            {preview && <div>
              <p>Собівартість: {preview.costPrice.toFixed(4)} ₴ · Ціна: {preview.salePrice.toFixed(2)} ₴</p>
              <CalculationInputs inputsJson={preview.inputsJson} fingerprint={preview.inputFingerprint} />
              <button type="button" disabled={!access.edit} onClick={() => void mutate(false)}>Зберегти знімок без активації</button>
            </div>}
          </fieldset>
          <button type="button" disabled={!ready} onClick={() => void download()}>Експорт приватних розрахунків JSON</button>
          <button type="button" disabled={!ready || !access.edit || history.activeCalculationId === null}
            onClick={() => { if (window.confirm('Вимкнути контекстну ціну й повернути глобальну для нових меню?')) void mutate(true, null); }}>
            Вимкнути контекстну ціну
          </button>
          <div className="operating-calculations__history">
            {history.calculations.map((row) => <article key={row.id}>
              <h4>Розрахунок #{row.id}{row.id === history.activeCalculationId ? ' · активний' : ''}</h4>
              <p>{row.createdAtUtc} · Собівартість {row.costPrice.toFixed(4)} ₴ · Націнка {row.margin}
                {row.marginType === 'amount' ? ' ₴' : ' %'} · Ціна {row.salePrice.toFixed(2)} ₴</p>
              <p>{history.currentInputFingerprint === row.inputFingerprint ? 'Вхідні дані актуальні' :
                'Вхідні дані змінилися або недоступні — нова активація заборонена'}</p>
              <CalculationInputs inputsJson={row.inputsJson} fingerprint={row.inputFingerprint} />
              <button type="button" disabled={!ready || !access.edit || row.id === history.activeCalculationId ||
                row.inputFingerprint !== history.currentInputFingerprint}
                onClick={() => { if (window.confirm(`Активувати ціну ${row.salePrice.toFixed(2)} ₴ для всіх контекстних залів цієї групи? Старі пропозиції не зміняться.`)) void mutate(true, row.id); }}>
                Активувати ціну #{row.id}
              </button>
            </article>)}
          </div>
          <details><summary>Історія публікацій ({history.publications.length})</summary>
            {history.publications.map((row) => <p key={row.id}>Версія {row.groupVersion} · {row.createdAtUtc} ·
              {row.calculationId === null ? ' глобальна ціна' : ` розрахунок #${row.calculationId}`}</p>)}
          </details>
        </>}
      </>}
    </section>
  );
}
